/**
 * Modelul vizual detaliat pentru condus: „Car Concept” (Khronos Group, CC BY 4.0, derivat dintr-un model CC0 de
 * Unity Fan), cu interior complet, materiale PBR și roți separate. Pentru fiecare mașină din joc e remodelat
 * proporțional: ampatamentul, ecartamentul, consolele față/spate, înălțimea și mărimea roților vin din fișa ei,
 * ca arcadele să cadă pe roțile din fizică. Modelele proprii, făcute în Blender după caietul de sarcini, îl înlocuiesc.
 */
import * as THREE from 'three/webgpu';
import type { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { tyreDiameterM } from '../data/calc';
import type { VehicleDefinition } from '../data/types';

// reperele modelului original (Y sus, Z înainte, X stânga), măsurate din geometrie
const M = { zFront: 1.486, zRear: -1.314, zNose: 2.417, zTail: -1.94, xWheel: 0.976, halfBody: 1.02, yWheel: 0.384, yRoof: 1.149, rWheel: 0.384, wWheel: 0.25 };
const WHEEL_NAMES: Record<string, number> = { WheelFrontL: 0, WheelFrontR: 1, WheelRearL: 2, WheelRearR: 3 };

let cached: Promise<THREE.Group> | null = null;

export function loadConceptScene(loader: GLTFLoader, url = '/models/concept.glb'): Promise<THREE.Group> {
  cached ??= loader.loadAsync(url).then((g) => g.scene as unknown as THREE.Group);
  return cached;
}

export interface ShapedPart { geometry: THREE.BufferGeometry; material: THREE.Material; wheel: number; spin: boolean; name: string }

/** Geometria modelului, transformată în proporțiile mașinii (în cadrul ei de repaus, pe sol, la mijlocul ampatamentului). */
export function shapeConcept(scene: THREE.Group, v: VehicleDefinition): ShapedPart[] {
  const d = v.dimensions;
  const L = d.lengthMm / 1000, W = d.widthMm / 1000, H = d.heightMm / 1000, WB = d.wheelbaseMm / 1000;
  const rF = (tyreDiameterM(v.tyres.front.size) ?? 0.62) / 2, rR = (tyreDiameterM(v.tyres.rear.size) ?? 0.62) / 2;
  const r = (rF + rR) / 2;
  const fo = (L - WB) * 0.55, ro = L - WB - fo;
  const sx = W / 2 / M.halfBody;
  const mapY = (y: number) => (y <= M.yWheel ? (y * r) / M.yWheel : r + ((y - M.yWheel) * (H - r)) / (M.yRoof - M.yWheel));
  const mapZ = (z: number) => {
    if (z >= M.zFront) return WB / 2 + ((z - M.zFront) * fo) / (M.zNose - M.zFront);
    if (z <= M.zRear) return -WB / 2 + ((z - M.zRear) * ro) / (M.zRear - M.zTail);
    return -WB / 2 + ((z - M.zRear) * WB) / (M.zFront - M.zRear);
  };
  scene.updateMatrixWorld(true);
  const parts: ShapedPart[] = [];
  const centers = new Map<number, THREE.Vector3>();
  scene.traverse((o) => {
    const k = WHEEL_NAMES[o.name];
    if (k !== undefined) centers.set(k, new THREE.Box3().setFromObject(o).getCenter(new THREE.Vector3()));
  });
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    let wheel = -1, pad = false;
    for (let a: THREE.Object3D | null = mesh; a; a = a.parent) {
      if (/BrakePad/.test(a.name)) pad = true;
      if (WHEEL_NAMES[a.name] !== undefined) { wheel = WHEEL_NAMES[a.name]; break; }
      const base = /^(Wheel(?:Front|Rear)[LR])/.exec(a.name);
      if (base && WHEEL_NAMES[base[1]] !== undefined) { wheel = WHEEL_NAMES[base[1]]; break; }
    }
    if (/^Axles/.test(mesh.name) || (mesh.parent && /^Axles/.test(mesh.parent.name))) return;
    const src = mesh.geometry;
    const g = new THREE.BufferGeometry();
    for (const name of ['position', 'normal', 'uv', 'tangent'] as const) {
      const a = src.getAttribute(name);
      if (!a) continue;
      const size = a.itemSize, out = new Float32Array(a.count * size);
      for (let i = 0; i < a.count; i++) for (let c = 0; c < size; c++) out[i * size + c] = a.getComponent(i, c);
      g.setAttribute(name, new THREE.BufferAttribute(out, size));
    }
    if (src.index) g.setIndex(new THREE.BufferAttribute(Uint32Array.from(src.index.array as ArrayLike<number>), 1));
    g.applyMatrix4(mesh.matrixWorld);
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    const p = pos.array as Float32Array;
    if (wheel >= 0) {
      // roata: centrată pe butuc, scalată la anvelopa din fișă (raza și lățimea)
      const c = centers.get(wheel)!;
      const front = wheel < 2;
      const rr = front ? rF : rR;
      const tw = Number((front ? v.tyres.front : v.tyres.rear).size.split('/')[0]) / 1000;
      for (let i = 0; i < pos.count; i++) {
        p[i * 3] = (p[i * 3] - c.x) * (tw / M.wWheel);
        p[i * 3 + 1] = (p[i * 3 + 1] - c.y) * (rr / M.rWheel);
        p[i * 3 + 2] = (p[i * 3 + 2] - c.z) * (rr / M.rWheel);
      }
    } else {
      for (let i = 0; i < pos.count; i++) {
        p[i * 3] *= sx;
        p[i * 3 + 1] = mapY(p[i * 3 + 1]);
        p[i * 3 + 2] = mapZ(p[i * 3 + 2]);
      }
    }
    pos.needsUpdate = true;
    g.computeVertexNormals();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    if (mats.length > 1 && g.index) {
      // materiale multiple: câte o parte pe grup
      for (const grp of src.groups) {
        const sub = g.clone();
        sub.setIndex(new THREE.BufferAttribute((g.index.array as Uint32Array).slice(grp.start, grp.start + grp.count), 1));
        parts.push({ geometry: sub, material: mats[grp.materialIndex ?? 0], wheel, spin: wheel >= 0 && !pad, name: mesh.name });
      }
      g.dispose();
    } else parts.push({ geometry: g, material: mats[0], wheel, spin: wheel >= 0 && !pad, name: mesh.name });
  });
  return parts;
}

/** Copie a materialului ca material de noduri (ca să primească deformarea), păstrând texturile și lacul. */
export function nodeMaterial(src: THREE.Material, paint: string | null, positionNode: unknown): THREE.MeshPhysicalNodeMaterial {
  const s = src as THREE.MeshPhysicalMaterial;
  const m = new THREE.MeshPhysicalNodeMaterial();
  const keys = ['color', 'map', 'normalMap', 'normalScale', 'roughness', 'roughnessMap', 'metalness', 'metalnessMap', 'aoMap', 'aoMapIntensity', 'emissive', 'emissiveMap', 'emissiveIntensity', 'alphaMap', 'transparent', 'opacity', 'side', 'alphaTest', 'depthWrite', 'clearcoat', 'clearcoatRoughness', 'clearcoatMap', 'clearcoatNormalMap', 'transmission', 'transmissionMap', 'ior', 'thickness', 'envMapIntensity', 'name'] as const;
  for (const k of keys) {
    const val = (s as unknown as Record<string, unknown>)[k];
    if (val === undefined || val === null) continue;
    const cur = (m as unknown as Record<string, unknown>)[k] as { copy?: (x: unknown) => void } | undefined;
    if (cur && typeof cur === 'object' && typeof cur.copy === 'function' && typeof val === 'object' && !(val as THREE.Texture).isTexture) cur.copy(val);
    else (m as unknown as Record<string, unknown>)[k] = val;
  }
  // geamurile cu transmisie sunt scumpe pe telefon: sticlă transparentă simplă
  if ((s.transmission ?? 0) > 0 || /glass/i.test(s.name)) Object.assign(m, { transmission: 0, transparent: true, opacity: 0.35, roughness: 0.05, metalness: 0.1, depthWrite: false });
  if (paint && /^paint 1/i.test(s.name)) { m.color.set(paint); m.metalness = 0.55; m.roughness = 0.3; m.clearcoat = 1; m.clearcoatRoughness = 0.05; }
  (m as unknown as { positionNode: unknown }).positionNode = positionNode;
  return m;
}
